import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

const LETTER_TO_POINTS_5 = {
  'A+': 5.0,
  'A': 4.75,
  'B+': 4.5,
  'B': 4.0,
  'C+': 3.5,
  'C': 3.0,
  'D+': 2.5,
  'D': 2.0,
  'F': 0.0,
};

const GRADE_OPTIONS_5 = [
  { value: 'A+', label: 'A+ (95–100)' },
  { value: 'A',  label: 'A (90-94)' },
  { value: 'B+', label: 'B+ (85–89)' },
  { value: 'B',  label: 'B (80-84)' },
  { value: 'C+', label: 'C+ (75–79)' },
  { value: 'C',  label: 'C (70-74)' },
  { value: 'D+', label: 'D+ (65–69)' },
  { value: 'D',  label: 'D (60-64)' },
  { value: 'F',  label: 'F (<60)' }
];

const CalculatorPage = () => {
  const [searchParams] = useSearchParams();
  const [calculatorType, setCalculatorType] = useState('basic');

  useEffect(() => {
    const type = searchParams.get('type');
    if (type) setCalculatorType(type);
  }, [searchParams]);

  const calculators = [
    { id: 'absence', label: 'الغياب' },
    { id: 'gpa', label: 'المعدل التراكمي' },
    { id: 'health', label: 'المسار الصحي' },
    { id: 'weighted', label: 'النسبة الموزونة' },
    { id: 'percentage', label: 'تحويل المعدل إلى نسبة' },
    { id: 'basic', label: 'آلة حاسبة' },
  ];

  const renderCalculator = () => {
    switch (calculatorType) {
      case 'absence':
        return <AbsenceCalculatorWixExact />;
      case 'gpa':
        return <GPACalculator />;
      case 'health':
        return <HealthPathWixExact />;
      case 'weighted':
        return <WeightedPercentageCalculator />;
      case 'percentage':
        return <PercentageCalculator />;
      default:
        return <BasicCalculator />;
    }
  };

  return (
    <div className="text-white bg-transparent relative z-10">
      <div className="max-w-4xl mx-auto px-4 pt-12 pb-24">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-white">الحاسبة</h1>
        </div>

        <div className="mb-8">
          <div className="flex flex-wrap justify-center gap-3" dir="rtl">
            {calculators.map((type) => (
              <button
                key={type.id}
                onClick={() => setCalculatorType(type.id)}
                className={`transition-all duration-300 ${
                  calculatorType === type.id
                    ? 'bg-gradient-to-br from-[rgba(40,227,218,0.5)] to-[rgba(40,227,218,0)] bg-[rgba(40,227,218,0.25)]'
                    : 'bg-transparent'
                } border border-[rgba(40,227,218,0.25)] rounded-xl text-white font-extrabold text-lg flex items-center justify-center`}
                style={{
                  padding: '13px 18px',
                  height: '44px',
                  fontFamily: 'Alexandria',
                  fontWeight: 400,
                  fontSize: '14px',
                  lineHeight: '100%',
                  whiteSpace: 'nowrap'
                }}
              >
                {type.label}
              </button>
            ))}
          </div>
        </div>

        <div className="p-6">
          {renderCalculator()}
        </div>
      </div>
    </div>
  );
};




const BasicCalculator = () => {
  const [display, setDisplay] = useState('0');
  const [previousValue, setPreviousValue] = useState(null);
  const [operation, setOperation] = useState(null);
  const [waitingForOperand, setWaitingForOperand] = useState(false);

  const inputDigit = (digit) => {
    if (waitingForOperand) {
      setDisplay(String(digit));
      setWaitingForOperand(false);
    } else {
      setDisplay(display === '0' ? String(digit) : display + digit);
    }
  };

  const inputDecimal = () => {
    if (waitingForOperand) {
      setDisplay('0.');
      setWaitingForOperand(false);
    } else if (!display.includes('.')) {
      setDisplay(display + '.');
    }
  };

  const clear = () => {
    setDisplay('0');
    setPreviousValue(null);
    setOperation(null);
    setWaitingForOperand(false);
  };

  const calculate = (a, b, op) => {
    switch (op) {
      case '+': return a + b;
      case '-': return a - b;
      case '×': return a * b;
      case '÷': return b === 0 ? NaN : a / b;
      default:  return b;
    }
  };

  const performOperation = (nextOperation) => {
    const inputValue = parseFloat(display);
    if (previousValue === null) {
      setPreviousValue(inputValue);
    } else if (operation) {
      const newValue = calculate(previousValue, inputValue, operation);
      setDisplay(Number.isFinite(newValue) ? String(newValue) : 'خطأ');
      setPreviousValue(newValue);
    }
    setWaitingForOperand(true);
    setOperation(nextOperation);
  };

  const equals = () => {
    if (previousValue === null || !operation) return;
    const inputValue = parseFloat(display);
    const newValue = calculate(previousValue, inputValue, operation);
    setDisplay(Number.isFinite(newValue) ? String(newValue) : 'خطأ');
    setPreviousValue(null);
    setOperation(null);
    setWaitingForOperand(false);
  };

  const plusMinus = () => {
    const v = parseFloat(display);
    if (Number.isFinite(v)) setDisplay(String(-v));
  };

  return (
    <>
      <div className="bg-[rgba(0,0,0,0.3)] rounded-2xl p-6 mb-6 text-right">
        <div className="text-4xl font-bold text-white min-h-[60px] flex items-center justify-end">{display}</div>
      </div>
      <div className="grid grid-cols-4 gap-3">
        <button onClick={clear} className="col-span-2 bg-[rgba(255,59,48,0.8)] hover:bg-[rgba(255,59,48,1)] text-white font-bold text-xl rounded-2xl p-4 transition-all">AC</button>
        <button onClick={plusMinus} className="bg-[rgba(40,227,218,0.3)] hover:bg-[rgba(40,227,218,0.5)] text-white font-bold text-xl rounded-2xl p-4 transition-all">±</button>
        <button onClick={() => performOperation('÷')} className="bg-[rgba(40,227,218,0.3)] hover:bg-[rgba(40,227,218,0.5)] text-white font-bold text-xl rounded-2xl p-4 transition-all">÷</button>

        {[7,8,9].map(n=>(
          <button key={n} onClick={()=>inputDigit(n)} className="bg-[rgba(255,255,255,0.1)] hover:bg-[rgba(255,255,255,0.2)] text-white font-bold text-xl rounded-2xl p-4 transition-all">{n}</button>
        ))}
        <button onClick={()=>performOperation('×')} className="bg-[rgba(40,227,218,0.3)] hover:bg-[rgba(40,227,218,0.5)] text-white font-bold text-xl rounded-2xl p-4 transition-all">×</button>

        {[4,5,6].map(n=>(
          <button key={n} onClick={()=>inputDigit(n)} className="bg-[rgba(255,255,255,0.1)] hover:bg-[rgba(255,255,255,0.2)] text-white font-bold text-xl rounded-2xl p-4 transition-all">{n}</button>
        ))}
        <button onClick={()=>performOperation('-')} className="bg-[rgba(40,227,218,0.3)] hover:bg-[rgba(40,227,218,0.5)] text-white font-bold text-xl rounded-2xl p-4 transition-all">-</button>

        {[1,2,3].map(n=>(
          <button key={n} onClick={()=>inputDigit(n)} className="bg-[rgba(255,255,255,0.1)] hover:bg-[rgba(255,255,255,0.2)] text-white font-bold text-xl rounded-2xl p-4 transition-all">{n}</button>
        ))}
        <button onClick={()=>performOperation('+')} className="bg-[rgba(40,227,218,0.3)] hover:bg-[rgba(40,227,218,0.5)] text-white font-bold text-xl rounded-2xl p-4 transition-all">+</button>

        <button onClick={()=>inputDigit(0)} className="col-span-2 bg-[rgba(255,255,255,0.1)] hover:bg-[rgba(255,255,255,0.2)] text-white font-bold text-xl rounded-2xl p-4 transition-all">0</button>
        <button onClick={inputDecimal} className="bg-[rgba(255,255,255,0.1)] hover:bg-[rgba(255,255,255,0.2)] text-white font-bold text-xl rounded-2xl p-4 transition-all">.</button>
        <button onClick={equals} className="bg-[rgba(40,227,218,0.8)] hover:bg-[rgba(40,227,218,1)] text-white font-bold text-xl rounded-2xl p-4 transition-all">=</button>
      </div>
    </>
  );
};






const AbsenceCalculatorWixExact = () => {
  const [weeks, setWeeks] = useState('16');
  const [lectures, setLectures] = useState('');
  const [allowedPercentage, setAllowedPercentage] = useState('25');
  const [result, setResult] = useState('');

  const toInt = (v) => {
    const n = parseInt(v, 10);
    return Number.isFinite(n) ? n : null;
  };
  const toFloat = (v) => {
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : null;
  };

  const lecturesWord = (n) => {
    if (n === 0) return '0 محاضرة';
    if (n === 1) return 'محاضرة واحدة';
    if (n === 2) return 'محاضرتان';
    if (n >= 3 && n <= 10) return `${n} محاضرات`;
    return `${n} محاضرة`;
  };

  const calculate = () => {
    const w = toInt(weeks);
    const l = toInt(lectures);
    const pct = toFloat(allowedPercentage);

    if (!(w >= 1 && w <= 20 && l >= 0 && pct >= 0)) {
      setResult('الرجاء إدخال أرقام صحيحة في جميع الحقول.');
      return;
    }
    const totalLectures = w * l;
    const allowed = Math.floor(totalLectures * (pct / 100));
    const line1 = `:مجموع المحاضرات خلال الفترة المحددة\n ${lecturesWord(totalLectures)}`;
    const line2 = `:غياب الطالب المسموح به\n ${lecturesWord(allowed)}`;
    setResult(`${line1}\n${line2}`);
  };

  const inputStyle = {
    padding: '13px 32px',
    height: '44px',
    border: '1px solid #FFFFFF',
    borderRadius: '12px',
    fontFamily: 'Alexandria',
    fontWeight: 800,
    fontSize: '16px',
    lineHeight: '100%',
    backgroundColor: 'transparent'
  };

  const selectStyle = {
    ...inputStyle,
    appearance: 'none',
    backgroundImage:
      "url(\"data:image/svg+xml;charset=US-ASCII,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='8'><path d='M1.41.59 6 5.17 10.59.59 12 2 6 8 0 2z' fill='%23FFFFFF'/></svg>\")",
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'left 16px center',
    backgroundSize: '12px 8px',
    paddingLeft: '40px',
    textAlign: 'center'
  };

  return (
    <div className="text-center">
      <h3 className="text-2xl font-bold text-white mb-8">الغيابات</h3>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8" dir="rtl">
        {                                               }
        <div className="relative">
          <select
            value={weeks}
            onChange={(e) => setWeeks(e.target.value)}
            className="w-full text-white"
            style={selectStyle}
          >
            {Array.from({ length: 20 }, (_, i) => String(i + 1)).map((w) => (
              <option key={w} value={w}>
                عدد الأسابيع: {w}
              </option>
            ))}
          </select>
        </div>

        <input
          type="number"
          value={lectures}
          onChange={(e)=>setLectures(e.target.value)}
          className="w-full text-white text-center [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none [-moz-appearance:textfield]"
          placeholder="المحاضرات الأسبوعية"
          style={inputStyle}
        />

        {                               }
        <div className="relative">
          <select
            value={allowedPercentage}
            onChange={(e)=>setAllowedPercentage(e.target.value)}
            className="w-full text-white"
            style={selectStyle}
          >
            <option value="10">10%</option>
            <option value="15">15%</option>
            <option value="20">20%</option>
            <option value="25">25% (افتراضي)</option>
            <option value="30">30%</option>
          </select>
        </div>
      </div>

      <div className="flex justify-center">
        <button
          onClick={calculate}
          className="text-white text-center"
          style={{
            padding:'13px 32px', width:'122px', height:'44px',
            background:'radial-gradient(43.64% 77.08% at 50% -10.42%, rgba(40, 227, 218, 0.5) 0%, rgba(40, 227, 218, 0) 100%), rgba(40, 227, 218, 0.25)',
            borderRadius:'12px', fontFamily:'Alexandria', fontWeight:800, fontSize:'18px', lineHeight:'100%',
            display:'flex', alignItems:'center', justifyContent:'center'
          }}
        >
          احسب
        </button>
      </div>

      {result && (
        <div className="mt-6 p-4 bg-[rgba(40,227,218,0.2)] border border-[#28e3da] rounded-xl whitespace-pre-line">
          <p className="text-white text-lg">{result}</p>
        </div>
      )}
    </div>
  );
};




const GPACalculator = () => {
  const [previousGPA, setPreviousGPA] = useState('');
  const [completedHours, setCompletedHours] = useState('');
  const [courses, setCourses] = useState([{ name: '', hours: '', grade: '' }]);
  const [gpa, setGpa] = useState('');

  const availableCourses = [
    { id: 1, name: 'رياضيات 101', code: 'MATH101', college: 'كلية العلوم' },
    { id: 2, name: 'فيزياء 101', code: 'PHYS101', college: 'كلية العلوم' },
    { id: 3, name: 'كيمياء 101', code: 'CHEM101', college: 'كلية العلوم' },
    { id: 4, name: 'أحياء 101', code: 'BIO101', college: 'كلية العلوم' },
    { id: 5, name: 'لغة إنجليزية 101', code: 'ENG101', college: 'كلية الآداب' },
    { id: 6, name: 'لغة عربية 101', code: 'ARB101', college: 'كلية الآداب' },
    { id: 7, name: 'تاريخ 101', code: 'HIST101', college: 'كلية الآداب' },
    { id: 8, name: 'جغرافيا 101', code: 'GEO101', college: 'كلية الآداب' },
    { id: 9, name: 'حاسوب 101', code: 'CS101', college: 'كلية الحاسب الآلي' },
    { id: 10, name: 'اقتصاد 101', code: 'ECON101', college: 'كلية الأعمال' },
    { id: 11, name: 'محاسبة 101', code: 'ACC101', college: 'كلية الأعمال' },
    { id: 12, name: 'إدارة أعمال 101', code: 'BUS101', college: 'كلية الأعمال' },
    { id: 13, name: 'قواعد بيانات', code: 'DB101', college: 'كلية الحاسب الآلي' },
    { id: 14, name: 'برمجة متقدمة', code: 'CS201', college: 'كلية الحاسب الآلي' },
    { id: 15, name: 'هندسة برمجيات', code: 'SE101', college: 'كلية الحاسب الآلي' },
    { id: 16, name: 'تشريح طبي', code: 'ANAT101', college: 'كلية الطب' },
    { id: 17, name: 'فسيولوجيا', code: 'PHYSIO101', college: 'كلية الطب' },
    { id: 18, name: 'كيمياء عضوية', code: 'ORG101', college: 'كلية العلوم' },
    { id: 19, name: 'كيمياء تحليلية', code: 'ANAL101', college: 'كلية العلوم' },
    { id: 20, name: 'إحصاء', code: 'STAT101', college: 'كلية العلوم' }
  ];

  const addCourse = () => {
    if (courses.length < 10) setCourses([...courses, { name: '', hours: '', grade: '' }]);
  };

  const removeCourse = (index) => {
    if (courses.length > 1) setCourses(courses.filter((_, i) => i !== index));
  };

  const updateCourse = (index, field, value) => {
    const newCourses = [...courses];
    newCourses[index][field] = value;
    setCourses(newCourses);
  };

  const [courseSearchQuery, setCourseSearchQuery] = useState('');
  const [showCourseSuggestions, setShowCourseSuggestions] = useState(false);
  const [activeCourseIndex, setActiveCourseIndex] = useState(null);

  const handleCourseSearch = (query, index) => {
    setCourseSearchQuery(query);
    setActiveCourseIndex(index);
    const newCourses = [...courses];
    newCourses[index].name = query;
    setCourses(newCourses);
    setShowCourseSuggestions(!!query.trim());
  };

  const handleCourseSelect = (course, index) => {
    const newCourses = [...courses];
    newCourses[index].name = course.name;
    setCourses(newCourses);
    setCourseSearchQuery('');
    setShowCourseSuggestions(false);
    setActiveCourseIndex(null);
  };

  const getFilteredCourses = () => {
    if (!courseSearchQuery.trim()) return availableCourses.slice(0,8);
    const q = courseSearchQuery.toLowerCase();
    return availableCourses
      .filter(course =>
        course.name.toLowerCase().includes(q) ||
        course.code.toLowerCase().includes(q) ||
        course.college.toLowerCase().includes(q)
      )
      .slice(0, 8);
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest('.course-search-container')) {
        setShowCourseSuggestions(false);
        setActiveCourseIndex(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const calculateGPA = () => {
    let totalPoints = 0;
    let totalHours = 0;

    if (previousGPA !== '' && completedHours !== '') {
      const prevGPA = parseFloat(previousGPA);
      const prevHours = parseFloat(completedHours);
      if (prevGPA >= 0 && prevGPA <= 5 && prevHours > 0) {
        totalPoints += prevHours * prevGPA;
        totalHours += prevHours;
      }
    }

    courses.forEach(course => {
      if (course.hours && course.grade) {
        const hours = parseFloat(course.hours);
        const points = LETTER_TO_POINTS_5[course.grade];
        if (!isNaN(hours) && points !== undefined) {
          totalPoints += hours * points;
          totalHours += hours;
        }
      }
    });

    if (totalHours > 0) setGpa((totalPoints / totalHours).toFixed(2));
    else setGpa('أدخل بيانات صحيحة');
  };

  const inputBase = {
    padding: '13px 32px', minHeight:'44px', border:'1px solid #FFFFFF', borderRadius:'12px',
    fontFamily:'Alexandria', fontWeight:400, fontSize:'16px', lineHeight:'100%', backgroundColor:'transparent'
  };

  return (
    <div className="text-center">
      <h3 className="text-2xl font-bold text-white mb-8">المعدل التراكمي (من 5)</h3>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <input type="number" value={previousGPA} onChange={(e)=>setPreviousGPA(e.target.value)}
          className="w-full text-white text-center" placeholder="المعدل السابق (من 5)"
          step="0.01" min="0" max="5" style={{...inputBase, fontWeight:800}}/>
        <input type="number" value={completedHours} onChange={(e)=>setCompletedHours(e.target.value)}
          className="w-full text-white text-center" placeholder="الساعات المجتازة"
          style={{...inputBase, fontWeight:800}}/>
      </div>

      <div className="text-right mb-8">
        <p className="text-white text-sm">لا يشترط المعدل السابق إذا رغبت بحسابه كمعدل فصلي فقط.</p>
      </div>

      <div className="space-y-4 mb-8">
        {courses.map((course, index) => (
          <div key={index} className="grid grid-cols-1 md:grid-cols-12 gap-4" dir="rtl">
            <div className="md:col-span-4 relative course-search-container">
              <input type="text" value={course.name || ''} onChange={(e)=>handleCourseSearch(e.target.value, index)}
                placeholder={`مقرر ${index + 1}`} className="w-full text-white text-center" style={inputBase}/>
              {showCourseSuggestions && activeCourseIndex === index && getFilteredCourses().length > 0 && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-[#081423] border border-white/20 rounded-[6px] shadow-lg z-[9999] max-h-64 overflow-y-auto">
                  {getFilteredCourses().map((opt)=>(
                    <div key={opt.id} className="px-4 py-3 hover:bg-white/10 cursor-pointer border-b border-white/10 last:border-b-0"
                      onClick={()=>handleCourseSelect(opt, index)}>
                      <div className="flex items-center gap-3">
                        <div className="w-6 h-6 rounded-full flex items-center justify-center"
                          style={{background:'radial-gradient(43.64% 77.08% at 50% -10.42%, rgba(40, 227, 218, 0.5) 0%, rgba(40, 227, 218, 0) 100%), rgba(40, 227, 218, 0.25)'}}/>
                        <div className="flex-1 text-right">
                          <div className="text-white text-sm font-medium">{opt.name}</div>
                          <div className="text-white/70 text-xs mt-1">{opt.code} - {opt.college}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="md:col-span-3">
              <input type="number" value={course.hours} onChange={(e)=>updateCourse(index,'hours',e.target.value)}
                className="w-full text-white text-center" placeholder="الساعات" style={inputBase}/>
            </div>

            <div className="md:col-span-5">
              <select value={course.grade} onChange={(e)=>updateCourse(index,'grade',e.target.value)}
                className="w-full text-white text-center"
                style={{...inputBase, appearance:'none', backgroundImage:'url("data:image/svg+xml;charset=US-ASCII,<svg width=\'12\' height=\'8\' xmlns=\'http://www.w3.org/2000/svg\'><path d=\'M1.41.59 6 5.17 10.59.59 12 2 6 8 0 2z\' fill=\'%23FFFFFF\'/></svg>")', backgroundRepeat:'no-repeat', backgroundPosition:'left 45px center', backgroundSize:'12px 8px'}}>
                <option value="" disabled>اختر الدرجة بالحروف</option>
                {GRADE_OPTIONS_5.map((g)=><option key={g.value} value={g.value}>{g.label}</option>)}
              </select>
            </div>
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between mb-8" dir="rtl">
        <button onClick={addCourse} className="text-white text-center"
          style={{padding:'13px 32px', height:'44px', border:'1px solid #FFFFFF', borderRadius:'12px',
            fontFamily:'Alexandria', fontWeight:800, fontSize:'16px', lineHeight:'100%', backgroundColor:'transparent', display:'flex', alignItems:'center', gap:'8px'}}
          disabled={courses.length >= 10}>
          <span>+</span><span>مقرر آخر</span>
        </button>

        {courses.length > 1 && (
          <button onClick={()=>removeCourse(courses.length-1)} className="text-white/80 underline">حذف آخر مقرر</button>
        )}
      </div>

      <div className="flex justify-center">
        <button onClick={calculateGPA} className="text-white text-center"
          style={{
            padding:'13px 32px', width:'122px', height:'44px',
            background:'radial-gradient(43.64% 77.08% at 50% -10.42%, rgba(40, 227, 218, 0.5) 0%, rgba(40, 227, 218, 0) 100%), rgba(40, 227, 218, 0.25)',
            borderRadius:'12px', fontFamily:'Alexandria', fontWeight:800, fontSize:'18px', lineHeight:'100%',
            display:'flex', alignItems:'center', justifyContent:'center'
          }}>
          احسب
        </button>
      </div>

      {gpa && (
        <div className="mt-6 p-4 bg-[rgba(40,227,218,0.2)] border border-[#28e3da] rounded-xl">
          <p className="text-white text-lg">المعدل التراكمي (من 5): <span className="font-bold text-[#28e3da]">{gpa}</span></p>
        </div>
      )}
    </div>
  );
};






const HealthPathWixExact = () => {
  const [s1, setS1] = useState('');
  const [s2, setS2] = useState('');
  const [s3, setS3] = useState('');
  const [qiyas, setQiyas] = useState('');
  const [tahsili, setTahsili] = useState('');
  const [gpa, setGpa] = useState('');
  const [result, setResult] = useState('');
  const [err, setErr] = useState('');

  const toNum = (v) => {
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : null;
  };

  const calc = () => {
    setErr('');
    const v1 = toNum(s1), v2 = toNum(s2), v3 = toNum(s3);
    const vq = toNum(qiyas), vt = toNum(tahsili), vg = toNum(gpa);

    if ([s1,s2,s3,qiyas,tahsili,gpa].some(x => x === '')) { setResult(''); setErr('رجاءً تعبئة جميع الحقول.'); return; }
    if ([v1,v2,v3,vq,vt].some(v => v === null || v < 0 || v > 100)) { setResult(''); setErr('الدرجات يجب أن تكون بين 0 و 100.'); return; }
    if (vg === null || vg < 0 || vg > 5) { setResult(''); setErr('المعدل التراكمي يجب أن يكون بين 0 و 5.'); return; }

    const gpaPercent = vg * 20;

    const total =
      (0.10 * v1) +
      (0.10 * v2) +
      (0.10 * v3) +
      (0.40 * gpaPercent) +
      (0.15 * vt) +
      (0.15 * vq);

    setResult(`معدلك بالنسبة المئوية هو:\n%${total.toFixed(2)}`);
  };

  const inputBase = {
    padding:'13px 32px', height:'44px', border:'1px solid #FFFFFF', borderRadius:'12px',
    fontFamily:'Alexandria', fontWeight:800, fontSize:'16px', lineHeight:'100%', backgroundColor:'transparent'
  };
  const inputCls = "w-full text-white text-center";

  return (
    <div className="text-center" dir="rtl">
      <h3 className="text-2xl font-bold text-white mb-8">المسار الصحي</h3>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <input type="number" value={s1} onChange={(e)=>setS1(e.target.value)} className={inputCls} placeholder="المادة المعيارية الأولى (من 100)" style={inputBase} min="0" max="100"/>
        <input type="number" value={gpa} onChange={(e)=>setGpa(e.target.value)} className={inputCls} placeholder="المعدل التراكمي (من 5)" style={inputBase} step="0.01" min="0" max="5"/>

        <input type="number" value={s2} onChange={(e)=>setS2(e.target.value)} className={inputCls} placeholder="المادة المعيارية الثانية (من 100)" style={inputBase} min="0" max="100"/>
        <input type="number" value={tahsili} onChange={(e)=>setTahsili(e.target.value)} className={inputCls} placeholder="درجة اختبار التحصيلي (من 100)" style={inputBase} min="0" max="100"/>

        <input type="number" value={s3} onChange={(e)=>setS3(e.target.value)} className={inputCls} placeholder="المادة المعيارية الثالثة (من 100)" style={inputBase} min="0" max="100"/>
        <input type="number" value={qiyas} onChange={(e)=>setQiyas(e.target.value)} className={inputCls} placeholder="درجة اختبار القدرات العامة (من 100)" style={inputBase} min="0" max="100"/>
      </div>

      <div className="flex justify-center">
        <button
          onClick={calc}
          className="text-white text-center"
          style={{
            padding:'13px 32px', width:'122px', height:'44px',
            background:'radial-gradient(43.64% 77.08% at 50% -10.42%, rgba(40, 227, 218, 0.5) 0%, rgba(40, 227, 218, 0) 100%), rgba(40, 227, 218, 0.25)',
            borderRadius:'12px', fontFamily:'Alexandria', fontWeight:800, fontSize:'18px', lineHeight:'100%',
            display:'flex', alignItems:'center', justifyContent:'center'
          }}
        >
          احسب
        </button>
      </div>

      {err && <p className="text-red-400 text-sm mt-3">{err}</p>}

      {result && !err && (
        <div className="mt-6 p-4 bg-[rgba(40,227,218,0.2)] border border-[#28e3da] rounded-xl whitespace-pre-line">
          <p className="text-white text-lg">{result}</p>
        </div>
      )}
    </div>
  );
};







const WeightedPercentageCalculator = () => {
  const [sco, setSco] = useState('');
  const [d8drat, setD8drat] = useState('');
  const [t79ele, setT79ele] = useState('');

  const [scop, setScop] = useState('40');
  const [d8dratp, setD8dratp] = useState('30');
  const [t79elep, setT79elep] = useState('30');

  const [result, setResult] = useState('');


  const PERCENT_OPTIONS = ['0','5','10','15','20','25','30','35','40','45','50','60','70','80','90','100'];

  const toFloat = (v) => {
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : 0;
  };

  const totalPct = Number(scop) + Number(d8dratp) + Number(t79elep);

  const calc = () => {

    if (totalPct !== 100) {
      setResult('الرجاء ضبط المعيارية بحيث يساوي مجموعها 100%.');
      return;
    }

    const total =
      toFloat(sco)    * (Number(scop)     / 100) +
      toFloat(d8drat) * (Number(d8dratp)  / 100) +
      toFloat(t79ele) * (Number(t79elep)  / 100);

    setResult('نسبتك الموزونة هي:\n%' + total.toFixed(2));
  };

  const inputBase = {
    padding:'13px 32px', height:'44px', border:'1px solid #FFFFFF', borderRadius:'12px',
    fontFamily:'Alexandria', fontWeight:800, fontSize:'16px', lineHeight:'100%', backgroundColor:'transparent'
  };

  const selectBase = {
    ...inputBase,
    appearance:'none',
    backgroundImage:
      "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='8'><path d='M1.41.59 6 5.17 10.59.59 12 2 6 8 0 2z' fill='%23FFFFFF'/></svg>\")",
    backgroundRepeat:'no-repeat',
    backgroundPosition:'left 16px center',
    backgroundSize:'12px 8px',
    textAlign:'center'
  };

  return (
    <div className="text-center" dir="rtl">
      <h3 className="text-2xl font-bold text-white mb-8">النسبة الموزونة</h3>

      {                    }
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        <input type="number" value={sco} onChange={(e)=>setSco(e.target.value)}
          className="w-full text-white text-center" placeholder="درجة الثانوية" style={inputBase}/>
        <input type="number" value={d8drat} onChange={(e)=>setD8drat(e.target.value)}
          className="w-full text-white text-center" placeholder="درجة القدرات" style={inputBase}/>
        <input type="number" value={t79ele} onChange={(e)=>setT79ele(e.target.value)}
          className="w-full text-white text-center" placeholder="درجة التحصيلي" style={inputBase}/>
      </div>

      {                           }
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-2">
        <div className="relative">
          <label className="block text-white text-sm mb-2 text-right">معيارية الثانوية (%)</label>
          <select value={scop} onChange={(e)=>setScop(e.target.value)}
            className="w-full text-white text-center" style={selectBase}>
            {PERCENT_OPTIONS.map(v=> <option key={'s-'+v} value={v}>{v}%</option>)}
          </select>
        </div>

        <div className="relative">
          <label className="block text-white text-sm mb-2 text-right">معيارية القدرات (%)</label>
          <select value={d8dratp} onChange={(e)=>setD8dratp(e.target.value)}
            className="w-full text-white text-center" style={selectBase}>
            {PERCENT_OPTIONS.map(v=> <option key={'q-'+v} value={v}>{v}%</option>)}
          </select>
        </div>

        <div className="relative">
          <label className="block text-white text-sm mb-2 text-right">معيارية التحصيلي (%)</label>
          <select value={t79elep} onChange={(e)=>setT79elep(e.target.value)}
            className="w-full text-white text-center" style={selectBase}>
            {PERCENT_OPTIONS.map(v=> <option key={'t-'+v} value={v}>{v}%</option>)}
          </select>
        </div>
      </div>

      {                     }
      <div className="mb-6 text-right">
        <span className={`text-sm ${totalPct === 100 ? 'text-green-400' : 'text-red-400'}`}>
          مجموع المعيارية: {totalPct}%
        </span>
      </div>

      {               }
      <div className="flex justify-center">
        <button onClick={calc} className="text-white text-center"
          style={{
            padding:'13px 32px', width:'122px', height:'44px',
            background:'radial-gradient(43.64% 77.08% at 50% -10.42%, rgba(40, 227, 218, 0.5) 0%, rgba(40, 227, 218, 0) 100%), rgba(40, 227, 218, 0.25)',
            borderRadius:'12px', fontFamily:'Alexandria', fontWeight:800, fontSize:'18px', lineHeight:'100%',
            display:'flex', alignItems:'center', justifyContent:'center'
          }}>
          احسب
        </button>
      </div>

      {             }
      {result && (
        <div className="mt-6 p-4 bg-[rgba(40,227,218,0.2)] border border-[#28e3da] rounded-xl whitespace-pre-line">
          <p className="text-white text-lg">{result}</p>
        </div>
      )}
    </div>
  );
};





const PercentageCalculator = () => {
  const [gba, setGba] = useState('');
  const [percentage, setPercentage] = useState('');
  const [error, setError] = useState('');

  const calculatePercentage = () => {
    const v = parseFloat(gba);
    if (Number.isFinite(v) && v >= 0 && v <= 5) {
      setError('');
      setPercentage((v * 20).toFixed(2) + '%');
    } else {
      setPercentage('');
      setError('أدخل معدل صحيح (من 5)');
    }
  };

  const inputBase = {
    padding: '13px 32px',
    height: '44px',
    border: '1px solid #FFFFFF',
    borderRadius: '12px',
    fontFamily: 'Alexandria',
    fontWeight: 800,
    fontSize: '16px',
    lineHeight: '100%',
    backgroundColor: 'transparent'
  };

  return (
    <div className="text-center">
      <h3 className="text-2xl font-bold text-white mb-8">تحويل المعدل إلى نسبة مئوية</h3>

      {                                              }
      <div className="mb-4">
        <div className="max-w-md w-full mx-auto">
          <input
            type="number"
            value={gba}
            onChange={(e) => setGba(e.target.value)}
            className="w-full text-white text-center"
            placeholder="المعدل من 5"
            step="0.01"
            min="0"
            max="5"
            style={inputBase}
            onKeyDown={(e) => { if (e.key === 'Enter') calculatePercentage(); }}
          />
          <div className="text-right mt-2">
            <p className="text-white text-sm">تظهر تلقائياً بعد الضغط على احسب</p>
          </div>
          {error && (
            <p className="text-red-400 text-sm mt-2 text-right">{error}</p>
          )}
        </div>
      </div>

      <div className="flex justify-center">
        <button
          onClick={calculatePercentage}
          className="text-white text-center"
          style={{
            padding: '13px 32px',
            width: '122px',
            height: '44px',
            background:
              'radial-gradient(43.64% 77.08% at 50% -10.42%, rgba(40, 227, 218, 0.5) 0%, rgba(40, 227, 218, 0) 100%), rgba(40, 227, 218, 0.25)',
            borderRadius: '12px',
            fontFamily: 'Alexandria',
            fontWeight: 800,
            fontSize: '18px',
            lineHeight: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          احسب
        </button>
      </div>

      {percentage && (
        <div className="mt-6 p-4 bg-[rgba(40,227,218,0.2)] border border-[#28e3da] rounded-xl">
          <p className="text-white text-lg">
            <span className="font-bold text-[#28e3da]">{percentage}</span>
          </p>
        </div>
      )}
    </div>
  );
};


export default CalculatorPage;
